import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { candidateJobsService } from '../services';

const CANDIDATE_JOBS_KEY = 'candidate-jobs';
const JOB_SEARCH_KEY = 'job-search';

/**
 * Hook pour récupérer les jobs d'un candidat
 */
export function useCandidateJobs(candidateId: number | undefined) {
  return useQuery({
    queryKey: [CANDIDATE_JOBS_KEY, candidateId],
    queryFn: () => candidateJobsService.getCandidateJobs(candidateId!),
    enabled: !!candidateId,
  });
}

/**
 * Hook pour assigner des jobs à un candidat
 */
export function useAssignJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, jobIds, source }: { candidateId: number; jobIds: number[]; source?: string }) =>
      candidateJobsService.assignJobs(candidateId, jobIds, source),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATE_JOBS_KEY, candidateId] });
      queryClient.invalidateQueries({ queryKey: ['candidates', 'detail', candidateId] });
    },
  });
}

/**
 * Hook pour retirer un job d'un candidat
 */
export function useRemoveJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, jobId }: { candidateId: number; jobId: number }) =>
      candidateJobsService.removeJob(candidateId, jobId),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATE_JOBS_KEY, candidateId] });
      queryClient.invalidateQueries({ queryKey: ['candidates', 'detail', candidateId] });
    },
  });
}

/**
 * Hook pour définir un job comme métier principal
 */
export function useSetPrimaryJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, jobId }: { candidateId: number; jobId: number }) =>
      candidateJobsService.setPrimaryJob(candidateId, jobId),
    onSuccess: (_, { candidateId }) => {
      queryClient.invalidateQueries({ queryKey: [CANDIDATE_JOBS_KEY, candidateId] });
    },
  });
}

/**
 * Hook pour rechercher des jobs dans la nomenclature (avec debounce côté composant)
 */
export function useSearchJobs(query: string, limit: number = 20) {
  return useQuery({
    queryKey: [JOB_SEARCH_KEY, query, limit],
    queryFn: () => candidateJobsService.searchJobs(query, limit),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes - nomenclature change rarement
  });
}
